import { useMemo } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import useSWR from 'swr';

import { ICityResponse, ICustomerAdminResponse, ISubUserResponse, IUserResponse, SubUserResponseObject } from 'src/types/user';
import axiosInstance, { endpoints, fetcher } from 'src/utils/axios';
import { client, useQuery } from './client';
import { components } from 'src/types/schema';


// ----------------------------------------------------------------------

// Super Admin Users
// useGetAccountAllUsers with URLSearchParams for query parameters
export function useGetAccountAllUsers(page: number, page_size: number, ordering: "asc" | "desc", search: string | undefined, role: string | undefined, restaurant: string | undefined) {
  const allowedValuesType = ["verified", "unverified"];

  const URL = '/partners/users/';
  const queryParams = new URLSearchParams({
    page: page.toString(),
    page_size: page_size.toString(),
    ordering: ordering === "asc" ? "created_at" : "-created_at",
  });

  // TODO: Add role filter
  // if (role && allowedValuesType.includes(role)) {
  //   queryParams.set('role', role);
  // }

  if (search) {
    queryParams.set('search', search);
  }

  if (restaurant) {
    queryParams.set('restaurant', restaurant);
  }

  const URL_WITH_PARAMS = `${URL}?${queryParams.toString()}`;

  const { data, isLoading, error, isValidating } = useQuery(URL_WITH_PARAMS as "/partners/users/");
  const memoizedValue = useMemo(
    () => ({
      users: data || { results: [], count: 0 },
      usersLoading: isLoading,
      usersError: error,
      usersValidating: isValidating,
      usersEmpty: !isLoading && !data?.results?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}



// Sub Admin Users


// Super Admin Users with URLSearchParams for query parameters
export function useGetAccountAllSubUsers(page: number, page_size: number, search: string | undefined) {
  const URL = endpoints.subusers.list;
  const queryParams = new URLSearchParams({
    page: page.toString(),
    page_size: page_size.toString(),
  });

  if (search && search.length > 0) {
    queryParams.set('q', search); // Assuming 'q' is the query parameter for search
  }

  const URL_WITH_PARAMS = `${URL}?${queryParams.toString()}`;

  const { data, isLoading, error, isValidating } = useSWR(URL_WITH_PARAMS, fetcher, { dedupingInterval: 30000 });
  const memoizedValue = useMemo(
    () => ({
      users: (data as ISubUserResponse) || { results: [] },
      usersLoading: isLoading,
      usersError: error,
      usersValidating: isValidating,
      usersEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}



export function useGetAllCities() {
  const { data, error, isLoading } = useQuery('/areas');

  const memoizedValue = useMemo(
    () => ({
      cities: data || [],
      citiesLoading: isLoading,
      citiesError: error,
      citiesValidating: false, // Removed since useQuery doesn't expose isValidating
      citiesEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading]
  );

  return memoizedValue;
}

export function useGetAllPaymentMethods() {
    const {data, isLoading, error} = useQuery('/partners/payment-methods', )

    return {
        paymentMethods: data,
        paymentMethodsLoading: isLoading,
        paymentMethodsError: error,
    }
}

export type UserResponse = components["schemas"]["UserRead"];
export function useGetUser(user_id: number) {
  const { data, isLoading, error, isValidating } = useQuery(`/partners/users/{id}/`, {
    params: {
      path: { id: user_id }
    }
  });
  const memoizedValue = useMemo(
    () => ({
      user: data,
      userLoading: isLoading,
      userError: error,
      userValidating: isValidating,
      userEmpty: !isLoading && !data,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetSubUser(sub_user_id: string) {
  const URL = endpoints.subusers.retrieve_user(sub_user_id);
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, { dedupingInterval: 30000 });
  const memoizedValue = useMemo(
    () => ({
      subuser: (data as SubUserResponseObject),
      subuserLoading: isLoading,
      subuserError: error,
      subuserValidating: isValidating,
      subuserEmpty: !isLoading && !data,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}



// Super User Sub Admin
export function useGetAccountCustomers() {
  const URL = endpoints.users.customer_admin;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(
    () => ({
      users: (data as ICustomerAdminResponse) || { results: [] },
      usersLoading: isLoading,
      usersError: error,
      usersValidating: isValidating,
      usersEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function updateUserDetails(userId: number, data: any) {
  const { data: responseData } = await client.PATCH('/partners/users/{id}/', {
    params: {
      path: { id: userId },
    },
    body: data,
  });
  return responseData;
}

export async function updateSubUserDetails(userId: number, data: any) {
  const URL = endpoints.subusers.update(`${userId}`);
  const response = await axiosInstance.put(URL, data);
  if (response.status !== 200) throw new Error(response.statusText);
  return response.data;
}

export async function createNewSubUser(data: any) {
  const URL = endpoints.subusers.create;
  const response = await axiosInstance.post(URL, data);
  if (response.status !== 201) throw new Error(response.statusText);
  return response.data;
}

export async function deleteSubUser(id: number) {
  const URL = endpoints.subusers.delete(id);
  const response = await axiosInstance.delete(URL);
  if (response.status !== 204) throw new Error(response.statusText);
  return response.data;
}

export function useGetAllPermissions() {
  const { data, isLoading, error, isValidating } = useQuery('/partners/permissions/', {
    params: {}
  });

  const memoizedValue = useMemo(
    () => ({
      permissions: data || [],
      permissionsLoading: isLoading,
      permissionsError: error,
      permissionsValidating: isValidating,
      permissionsEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}
export function useGetAllRoles() {
  const { data, isLoading, error, isValidating } = useQuery('/partners/roles/', {
    params: {}
  });

  const memoizedValue = useMemo(
    () => ({
      roles: data || [],
      rolesLoading: isLoading,
      rolesError: error,
      rolesValidating: isValidating,
      rolesEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}
